import { ReactSVG } from "react-svg";
import "../Components/PagesCompoents/Categories/styles/Index.css";
import NotFoundSVG from "../assets/svg/NotFound.svg";
import { useDispatch, useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import "../Components/PagesCompoents/TicketsPage/style/Tickets.css";
import {
  FilterEvent,
  GetAllEvents,
  GetUserCreatedEvents,
  SearchEvents,
  SearchEventsByCategory,
} from "../redux/actions/Events";
import FeaturedEvent from "../Components/EventCard/LatestCard/FeaturedEvent";
import VerticalExpediaBanner from "../Components/expedia/VerticalExpediaBanner";
import HorizontalExpediaBanner from "../Components/expedia/HorizontalExpediaBanner";
import LatestLoading from "../Components/Loading/LatestLoading";
import { useAppContext } from "../redux/context";
import {
  TicketsFilterPanel,
  TicketsListingHeader,
  TicketsMobileFilterDrawer,
} from "../Components/PagesCompoents/TicketsPage/TicketsPageFilterUI";
import {
  applyClientPriceFilters,
  eventMatchesDatePreset,
  eventMatchesDateRange,
  getEventDateForFilter,
  isPriceFilterActive,
  sortTicketEvents,
} from "../Components/PagesCompoents/TicketsPage/ticketsPageHelpers";
import { eventMatchesCity } from "../Components/PagesCompoents/TicketsPage/ticketsFilterCities";
import { useTicketsListingPrices } from "../Components/PagesCompoents/TicketsPage/useTicketsListingPrices";
import MarketplaceNewUIFooter from "../Components/Home/MarketplaceNewUI/MarketplaceNewUIFooter";
import {
  TICKETS_CATEGORY_UNAVAILABLE,
  resolveTicketsCategoryFromSearchParams,
} from "../Components/PagesCompoents/TicketsPage/ticketsCategoryCatalog";
import NotFoundLodaing from "../Components/Loading/NotFoundLodaing";

const Tickets = () => {
  const isLoading = useSelector((state) => state.events.isLoading);
  const total_entries = useSelector((state) => state.events.total_entries);
  const CopyofAllEvents = useSelector((state) => state.events.CopyofAllEvents);
  const UserLocationdata = useSelector((state) => state.user.userlocation);
  const AllEvents = useSelector((state) => state.events.AllEvents);
  const Filterserachevents = useSelector((state) => state.events.Filterserachevents);
  const serachevents = useSelector((state) => state.events.serachevents);
  const userCreatedEvents = useSelector((state) => state.events.userCreatedEvents);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [ref, inView] = useInView({
    threshold: true,
  });

  const {page, setPage} = useAppContext();
  const [value, setValue] = useState("Any Dates");
  const [isFilter, setIsFilter] = useState(false);
  const [startdate, setstartdate] = useState("");
  const [enddate, setenddate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("Newest");
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [freeOnly, setFreeOnly] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState("All Cities");
  const [apiFilterParams, setApiFilterParams] = useState(null);

  const headerSearchQuery = (searchParams.get("search") || "").trim();
  const isHeaderSearchMode = Boolean(headerSearchQuery);

  const activeCategory = useMemo(
    () =>
      resolveTicketsCategoryFromSearchParams(
        searchParams.get("category"),
        searchParams.get("categoryId")
      ),
    [searchParams]
  );
  const isCategoryMode = Boolean(activeCategory?.id);
  const categoryUnavailable =
    activeCategory?.name &&
    TICKETS_CATEGORY_UNAVAILABLE.has(activeCategory.name);

  useEffect(() => {
    if (!activeCategory?.id) return;
    setIsFilter(false);
    setApiFilterParams(null);
    setPage(1);
    dispatch(SearchEventsByCategory(activeCategory.id));
  }, [activeCategory?.id, dispatch, setPage]);

  useEffect(() => {
    if (!headerSearchQuery) return;
    setSearchTerm(headerSearchQuery);
    setIsFilter(false);
    setApiFilterParams(null);
    setPage(1);
    dispatch(SearchEvents(headerSearchQuery, UserLocationdata));
  }, [headerSearchQuery, UserLocationdata, dispatch, setPage]);

  const applyEventFilters = (events) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (events || []).filter((event) => {
      const hasTickets =
        event._isUserCreated ||
        (event.available_count && event.available_count > 0) ||
        (event.products_count && event.products_count > 0);
      if (!hasTickets) return false;

      if (!eventMatchesCity(event, selectedCity)) return false;

      const eventDate = getEventDateForFilter(event);
      if (eventDate && eventDate < today) return false;

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const eventName = (event.name || "").toLowerCase();
        const venueName = (event.venue?.name || "").toLowerCase();
        const performerNames =
          event.performances
            ?.map((p) => (p.performer?.name || "").toLowerCase())
            .join(" ") || "";
        if (
          !eventName.includes(term) &&
          !venueName.includes(term) &&
          !performerNames.includes(term)
        ) {
          return false;
        }
      }

      if (startdate || enddate) {
        return eventMatchesDateRange(event, startdate, enddate);
      }

      return eventMatchesDatePreset(event, value);
    });
  };

  // Combine TEVO events and user-created events, removing duplicates
  const combinedEvents = useMemo(() => {
    const allEvents = [
      ...(AllEvents || []),
      ...(userCreatedEvents || [])
    ];

    // Remove duplicates based on event ID or name
    const uniqueEvents = allEvents.filter((event, index, self) => {
      // For user-created events, check the inner event data
      const eventId = event?.id || event?.event?.[0]?.id || event?._id;
      const eventName = event?.name || event?.event?.[0]?.name;

      return index === self.findIndex(e => {
        const otherId = e?.id || e?.event?.[0]?.id || e?._id;
        const otherName = e?.name || e?.event?.[0]?.name;

        // Match by ID if available, otherwise by name
        return eventId && otherId ? eventId === otherId : eventName === otherName;
      });
    });

    return uniqueEvents;
  }, [AllEvents, userCreatedEvents]);

  // Sort events by creation date (newest first)
  const sortedCombinedEvents = useMemo(() => {
    return [...combinedEvents].sort((a, b) => {
      // For user-created events, use MongoDB _id timestamp
      // For TEVO events, use occurs_at or updated_at
      let dateA, dateB;

      if (a._isUserCreated && a._id) {
        // For user-created events, try to use occurs_at first, then fall back to ObjectId timestamp
        dateA = new Date(a.occurs_at || a.updated_at || 0);
        // If date is invalid, try ObjectId timestamp
        if (isNaN(dateA.getTime()) && typeof a._id === 'string' && a._id.length === 24) {
          try {
            const timestampA = parseInt(a._id.substring(0, 8), 16) * 1000;
            dateA = new Date(timestampA);
          } catch (e) {
            dateA = new Date(0); // Fallback to epoch
          }
        }
      } else {
        dateA = new Date(a.occurs_at || a.updated_at || 0);
      }

      if (b._isUserCreated && b._id) {
        // For user-created events, try to use occurs_at first, then fall back to ObjectId timestamp
        dateB = new Date(b.occurs_at || b.updated_at || 0);
        // If date is invalid, try ObjectId timestamp
        if (isNaN(dateB.getTime()) && typeof b._id === 'string' && b._id.length === 24) {
          try {
            const timestampB = parseInt(b._id.substring(0, 8), 16) * 1000;
            dateB = new Date(timestampB);
          } catch (e) {
            dateB = new Date(0); // Fallback to epoch
          }
        }
      } else {
        dateB = new Date(b.occurs_at || b.updated_at || 0);
      }

      return dateB - dateA; // Newest first
    });
  }, [combinedEvents]);

  const filteredEvents = useMemo(() => {
    return applyEventFilters(sortedCombinedEvents);
  }, [sortedCombinedEvents, searchTerm, value, selectedCity, startdate, enddate]);

  const categoryFilteredEvents = useMemo(() => {
    if (!isCategoryMode) return [];
    return applyEventFilters(serachevents);
  }, [
    isCategoryMode,
    serachevents,
    searchTerm,
    value,
    selectedCity,
    startdate,
    enddate,
  ]);

  const headerSearchFilteredEvents = useMemo(() => {
    if (!isHeaderSearchMode) return [];
    const fromApi = applyEventFilters(serachevents || []);
    const fromCommunity = applyEventFilters(userCreatedEvents || []);
    const seen = new Set();
    return [...fromApi, ...fromCommunity].filter((event) => {
      const id = event?.id ?? event?._id ?? event?.event?.[0]?.id;
      const key = id != null ? String(id) : event?.name;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [
    isHeaderSearchMode,
    serachevents,
    userCreatedEvents,
    searchTerm,
    value,
    selectedCity,
    startdate,
    enddate,
  ]);


  useMemo(() => {
    if (inView) {
      setPage(page + 1);
    }
  }, [inView]);


  useMemo(() => {
    if (isCategoryMode || isHeaderSearchMode) return;
    if (!isFilter) {
      if (page === 0) {
        const newp = 1;
        dispatch(GetAllEvents(newp, UserLocationdata));
      } else {
        dispatch(GetAllEvents(page, UserLocationdata));
      }
    } else if (apiFilterParams) {
      dispatch(
        FilterEvent(
          apiFilterParams.startDate,
          apiFilterParams.endDate,
          page,
          apiFilterParams.location,
          false
        )
      );
    }
  }, [page, isFilter, apiFilterParams, isCategoryMode, isHeaderSearchMode]);

  // Fetch user-created events on component mount
  useEffect(() => {
    dispatch(GetUserCreatedEvents());
  }, [dispatch]);


  const eventsBeforePrice = useMemo(() => {
    let eventsToDisplay;

    if (isHeaderSearchMode) {
      eventsToDisplay = headerSearchFilteredEvents;
    } else if (isCategoryMode) {
      eventsToDisplay = categoryFilteredEvents;
    } else if (isFilter) {
      const tevoFilteredEvents = (Filterserachevents || []).filter(
        (event) =>
          event._isUserCreated ||
          (event.available_count && event.available_count > 0) ||
          (event.products_count && event.products_count > 0)
      );

      const userCreatedFiltered =
        userCreatedEvents?.filter((event) => {
          if (startdate || enddate) {
            return eventMatchesDateRange(event, startdate, enddate);
          }
          return eventMatchesDatePreset(event, value);
        }) || [];

      eventsToDisplay = [...tevoFilteredEvents, ...userCreatedFiltered].filter(
        (event) => {
          if (!eventMatchesCity(event, selectedCity)) return false;
          if (startdate || enddate) {
            return eventMatchesDateRange(event, startdate, enddate);
          }
          if (value !== "Any Dates") {
            return eventMatchesDatePreset(event, value);
          }
          return true;
        }
      );
    } else {
      eventsToDisplay = filteredEvents || [];
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      eventsToDisplay = eventsToDisplay.filter((event) => {
        const eventName = (event.name || "").toLowerCase();
        const venueName = (event.venue?.name || "").toLowerCase();
        const performerNames =
          event.performances
            ?.map((p) => (p.performer?.name || "").toLowerCase())
            .join(" ") || "";

        return (
          eventName.includes(term) ||
          venueName.includes(term) ||
          performerNames.includes(term)
        );
      });
    }

    return eventsToDisplay;
  }, [
    isHeaderSearchMode,
    headerSearchFilteredEvents,
    isCategoryMode,
    categoryFilteredEvents,
    isFilter,
    Filterserachevents,
    filteredEvents,
    userCreatedEvents,
    startdate,
    enddate,
    searchTerm,
    selectedCity,
    value,
  ]);

  const needsListingPrices =
    isPriceFilterActive(priceRange, freeOnly) ||
    sortBy === "Price: Low to High" ||
    sortBy === "Price: High to Low";

  const { listingPrices, fetching: pricesFetching } = useTicketsListingPrices(
    eventsBeforePrice,
    needsListingPrices
  );

  const displayEvents = useMemo(() => {
    const priceFiltered = applyClientPriceFilters(
      eventsBeforePrice,
      priceRange,
      freeOnly,
      listingPrices
    );
    return sortTicketEvents(priceFiltered, sortBy, listingPrices);
  }, [
    eventsBeforePrice,
    priceRange,
    freeOnly,
    listingPrices,
    sortBy,
  ]);

  const showPriceLoading =
    needsListingPrices && pricesFetching && eventsBeforePrice.length > 0;

  const filterPanelProps = {
    value,
    setValue,
    startdate,
    setstartdate,
    enddate,
    setenddate,
    setIsFilter,
    setpage: setPage,
    priceRange,
    setPriceRange,
    freeOnly,
    setFreeOnly,
    userLocationData: UserLocationdata,
    selectedCity,
    setSelectedCity,
    onApplyApiFilter: setApiFilterParams,
    onClearApiFilter: () => setApiFilterParams(null),
    activeCategoryId: activeCategory?.id ?? null,
  };

  const performTicketsSearch = useCallback(
    async (query) => {
      const q = String(query || "").trim();
      setIsFilter(false);
      setApiFilterParams(null);
      setPage(1);
      if (!q) {
        navigate("/tickets");
        dispatch(GetAllEvents(1, UserLocationdata));
        return;
      }
      navigate(`/tickets?search=${encodeURIComponent(q)}`);
      await dispatch(SearchEvents(q, UserLocationdata));
    },
    [navigate, dispatch, UserLocationdata, setPage]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = searchTerm.trim();
      const urlQ = (searchParams.get("search") || "").trim();
      if (q === urlQ) return;
      performTicketsSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, searchParams, performTicketsSearch]);

  const displaySearchQuery = searchTerm.trim() || headerSearchQuery;
  const showSearchMode = Boolean(displaySearchQuery);

  const resetAllFilters = () => {
    setSearchTerm("");
    setValue("Any Dates");
    setSortBy("Newest");
    setPriceRange([0, 500]);
    setFreeOnly(false);
    setstartdate("");
    setenddate("");
    setSelectedCity("All Cities");
    setApiFilterParams(null);
    setIsFilter(false);
    setPage(1);
    navigate("/tickets");
    dispatch(GetAllEvents(1, UserLocationdata));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="min-h-screen bg-[#0F172A] ticket_page max-w-[2000px] mx-auto"
    >
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="px-4 sm:px-6 lg:px-8 pt-4"
      >
        <HorizontalExpediaBanner />
      </motion.div>

      <TicketsListingHeader
        eventCount={displayEvents?.length || 0}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortBy={sortBy}
        setSortBy={setSortBy}
        onOpenMobileFilters={() => setMobileFilterOpen(true)}
        priceFilterHint={
          showPriceLoading ? "Updating prices for your filter…" : null
        }
        pageTitle={
          showSearchMode
            ? `Search: ${displaySearchQuery}`
            : activeCategory
              ? activeCategory.name
              : "All Events"
        }
        categorySubtitle={
          showSearchMode
            ? "Event search results"
            : activeCategory?.parentName
              ? `Under ${activeCategory.parentName}`
              : isCategoryMode
                ? "Category"
                : null
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.08 }}
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-4 gap-8"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.06, delayChildren: 0.05 },
            },
          }}
        >
          <motion.aside
            variants={{ hidden: { opacity: 0, x: -12 }, visible: { opacity: 1, x: 0 } }}
            className="tickets-filter-sidebar hidden lg:block lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:overflow-x-hidden lg:overscroll-y-contain space-y-6 pr-1"
          >
            <TicketsFilterPanel {...filterPanelProps} />
            <VerticalExpediaBanner />
          </motion.aside>

          <motion.div
            variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
            className="lg:col-span-3"
          >
            {categoryUnavailable ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-20"
              >
                <NotFoundLodaing />
              </motion.div>
            ) : (isLoading || showPriceLoading) &&
            (!displayEvents || displayEvents.length === 0) ? (
              <motion.div
                className="flex flex-wrap gap-5 justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                  <LatestLoading key={i} />
                ))}
              </motion.div>
            ) : displayEvents && displayEvents.length > 0 ? (
              <>
                <motion.div
                  className="flex flex-wrap gap-5 justify-center sm:justify-start mb-5"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.04 } },
                  }}
                >
                  {displayEvents.map((item, index) => (
                    <motion.div
                      key={item?.id || item?._id || index}
                      variants={{
                        hidden: { opacity: 0, y: 16 },
                        visible: { opacity: 1, y: 0 },
                      }}
                    >
                      <FeaturedEvent item={item} index={index + 1} />
                    </motion.div>
                  ))}
                </motion.div>
                {!isCategoryMode &&
                !isHeaderSearchMode &&
                !isFilter
                  ? CopyofAllEvents &&
                    value === "Any Dates" &&
                    AllEvents &&
                    AllEvents?.length - 1 && <motion.div ref={ref} />
                  : !isCategoryMode &&
                    !isHeaderSearchMode &&
                    Filterserachevents &&
                    Filterserachevents?.length - 1 &&
                    Filterserachevents?.length < total_entries && (
                      <motion.div ref={ref} />
                    )}
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-20"
              >
                <motion.div className="flex justify-center mb-6">
                  <ReactSVG src={NotFoundSVG} />
                </motion.div>
                <p className="text-white/30 text-sm">No events match your filters</p>
                <button
                  type="button"
                  className="inline-block mt-4 text-sm text-red-500 font-semibold hover:underline"
                  onClick={resetAllFilters}
                >
                  Reset filters
                </button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </motion.div>

      <TicketsMobileFilterDrawer
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        filterPanelProps={filterPanelProps}
      />

      <MarketplaceNewUIFooter />
    </motion.div>
  );
};

export default Tickets;
